import React, { useState, useEffect } from 'react';
import { Briefcase, AlertCircle, Plus, X, FileText, Upload, Eye, Download, Share2, Trash2, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { motion } from 'framer-motion';

interface InsurancePolicy {
  id: string;
  type: 'life' | 'other';
  insurance_category?: string;
  provider: string;
  policy_number: string;
  coverage_amount: string;
  premium: string;
  payment_frequency: string;
  start_date: string;
  renewal_date: string;
  beneficiaries?: string[];
  notes?: string;
  document_url?: string;
  // Additional fields for other insurance types
  coverage_type?: string;
  waiting_period?: string;
  benefit_period?: string;
  deductible?: string;
  co_pay?: string;
  out_of_pocket_max?: string;
  network_type?: string;
  prescription_coverage?: boolean;
  dental_coverage?: boolean;
  vision_coverage?: boolean;
  roadside_assistance?: boolean;
  rental_car_coverage?: boolean;
  towing_coverage?: boolean;
  liability_coverage?: string;
  collision_coverage?: string;
  comprehensive_coverage?: string;
  uninsured_motorist?: string;
  property_coverage?: string;
  liability_limit?: string;
  medical_payments?: string;
  personal_injury_protection?: string;
}

interface InsuranceDocument {
  id: string;
  user_id: string;
  policy_type: string;
  provider: string;
  policy_number: string;
  coverage_amount?: string;
  premium?: string;
  payment_frequency: string;
  start_date?: string;
  renewal_date?: string;
  beneficiaries?: string[];
  notes?: string;
  file_path?: string;
  file_url?: string;
  file_name?: string;
  file_size?: number;
  file_type?: string;
  additional_data?: any;
  status: string;
  last_reviewed: string;
  created_at: string;
  updated_at: string;
}

const INSURANCE_CATEGORIES = [
  { value: 'health', label: 'Health Insurance', icon: '🏥' },
  { value: 'auto', label: 'Auto Insurance', icon: '🚗' },
  { value: 'home', label: 'Home Insurance', icon: '🏠' },
  { value: 'renters', label: 'Renters Insurance', icon: '🏢' },
  { value: 'pet', label: 'Pet Insurance', icon: '🐾' },
  { value: 'travel', label: 'Travel Insurance', icon: '✈️' },
  { value: 'umbrella', label: 'Umbrella Insurance', icon: '☂️' },
  { value: 'professional', label: 'Professional Liability', icon: '💼' },
  { value: 'cyber', label: 'Cyber Insurance', icon: '💻' },
  { value: 'disability', label: 'Disability Insurance', icon: '🦽' },
  { value: 'long_term_care', label: 'Long-term Care', icon: '🏥' },
  { value: 'accident', label: 'Accident Insurance', icon: '🚨' },
  { value: 'critical_illness', label: 'Critical Illness', icon: '⚕️' },
  { value: 'dental', label: 'Dental Insurance', icon: '🦷' },
  { value: 'vision', label: 'Vision Insurance', icon: '👁️' },
  { value: 'other', label: 'Other', icon: '📋' }
];

export function InsurancePolicies() {
  const { user } = useAuth();
  const [showLifeModal, setShowLifeModal] = useState(false);
  const [showOtherInsuranceModal, setShowOtherInsuranceModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [policies, setPolicies] = useState<InsurancePolicy[]>([]);
  const [documents, setDocuments] = useState<InsuranceDocument[]>([]);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [pdfViewerUrl, setPdfViewerUrl] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareDocId, setShareDocId] = useState<string | null>(null);
  const [shareForm, setShareForm] = useState({
    email: '',
    accessLevel: 'view',
    note: ''
  });
  const [expandedDocs, setExpandedDocs] = useState<Set<string>>(new Set());

  const [lifeFormData, setLifeFormData] = useState({
    provider: '',
    policy_number: '',
    coverage_amount: '',
    premium: '',
    payment_frequency: 'monthly',
    start_date: '',
    renewal_date: '',
    beneficiaries: [''],
    notes: '',
    document_url: '',
    file_path: '',
    file_name: '',
    file_size: 0,
    file_type: ''
  });

  const [otherInsuranceFormData, setOtherInsuranceFormData] = useState({
    insurance_category: '',
    provider: '',
    policy_number: '',
    coverage_amount: '',
    premium: '',
    payment_frequency: 'monthly',
    start_date: '',
    renewal_date: '',
    beneficiaries: [''],
    notes: '',
    document_url: '',
    file_path: '',
    file_name: '',
    file_size: 0,
    file_type: '',
    coverage_type: '',
    deductible: '',
    co_pay: '',
    out_of_pocket_max: '',
    network_type: '',
    prescription_coverage: false,
    dental_coverage: false,
    vision_coverage: false,
    liability_coverage: '',
    collision_coverage: '',
    comprehensive_coverage: '',
    uninsured_motorist: '',
    property_coverage: '',
    liability_limit: '',
    waiting_period: '',
    benefit_period: '',
    roadside_assistance: false,
    rental_car_coverage: false,
    towing_coverage: false,
    medical_payments: '',
    personal_injury_protection: ''
  });

  const loadPolicies = async (background = false) => {
    if (!user) return;
    try {
      if (!background) setLoading(true);
      // Load old policies (for backward compatibility)
      const { data: oldPolicies, error: oldError } = await supabase
        .from('insurance_policies')
        .select('*')
        .eq('user_id', user.id)
        .in('type', ['life', 'other'])
        .order('created_at', { ascending: false });
      if (oldError) throw oldError;
      setPolicies(oldPolicies || []);

      // Load new documents
      const { data: newDocs, error: newError } = await supabase
        .from('insurance_documents')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (newError) throw newError;
      setDocuments(newDocs || []);
    } catch (err) {
      if (!background) setError(err instanceof Error ? err.message : 'Failed to load policies');
    } finally {
      if (!background) setLoading(false);
    }
  };

  useEffect(() => {
    loadPolicies();
  }, [user]);

  useEffect(() => {
    const handler = () => { if (user) loadPolicies(true); };
    window.addEventListener('refresh-legal', handler);
    return () => window.removeEventListener('refresh-legal', handler);
  }, [user]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, setFormData: any, formData: any) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 50 * 1024 * 1024) {
      setError('File size must be less than 50MB');
      return;
    }
    
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setError('File type not supported. Please upload PDF, DOC, DOCX, or image files.');
      return;
    }
    
    try {
      setError(null);
      setUploading(true);
      const filePath = `${user?.id}/insurance/${Date.now()}_${file.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('insurance-documents')
        .upload(filePath, file);
      if (uploadError) throw uploadError;
      
      const { data: urlData } = supabase.storage
        .from('insurance-documents')
        .getPublicUrl(filePath);
      
      setFormData({ 
        ...formData, 
        file_path: filePath,
        file_url: urlData.publicUrl,
        file_name: file.name,
        file_size: file.size,
        file_type: file.type
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveLifeInsurance = async () => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from('insurance_documents')
        .insert([{
          user_id: user?.id,
          policy_type: 'life',
          provider: lifeFormData.provider,
          policy_number: lifeFormData.policy_number,
          coverage_amount: lifeFormData.coverage_amount,
          premium: lifeFormData.premium,
          payment_frequency: lifeFormData.payment_frequency,
          start_date: lifeFormData.start_date,
          renewal_date: lifeFormData.renewal_date,
          beneficiaries: lifeFormData.beneficiaries,
          notes: lifeFormData.notes,
          file_path: lifeFormData.file_path,
          file_url: lifeFormData.file_url,
          file_name: lifeFormData.file_name,
          file_size: lifeFormData.file_size,
          file_type: lifeFormData.file_type
        }]);
      if (error) throw error;
      setShowLifeModal(false);
      setLifeFormData({
        provider: '',
        policy_number: '',
        coverage_amount: '',
        premium: '',
        payment_frequency: 'monthly',
        start_date: '',
        renewal_date: '',
        beneficiaries: [''],
        notes: '',
        document_url: '',
        file_path: '',
        file_name: '',
        file_size: 0,
        file_type: ''
      });
      // Reload documents
      const { data } = await supabase
        .from('insurance_documents')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });
      setDocuments(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save life insurance policy');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOtherInsurance = async () => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from('insurance_documents')
        .insert([{
          user_id: user?.id,
          policy_type: otherInsuranceFormData.insurance_category,
          provider: otherInsuranceFormData.provider,
          policy_number: otherInsuranceFormData.policy_number,
          coverage_amount: otherInsuranceFormData.coverage_amount,
          premium: otherInsuranceFormData.premium,
          payment_frequency: otherInsuranceFormData.payment_frequency,
          start_date: otherInsuranceFormData.start_date,
          renewal_date: otherInsuranceFormData.renewal_date,
          beneficiaries: otherInsuranceFormData.beneficiaries,
          notes: otherInsuranceFormData.notes,
          file_path: otherInsuranceFormData.file_path,
          file_url: otherInsuranceFormData.file_url,
          file_name: otherInsuranceFormData.file_name,
          file_size: otherInsuranceFormData.file_size,
          file_type: otherInsuranceFormData.file_type,
          additional_data: {
            coverage_type: otherInsuranceFormData.coverage_type,
            deductible: otherInsuranceFormData.deductible,
            co_pay: otherInsuranceFormData.co_pay,
            out_of_pocket_max: otherInsuranceFormData.out_of_pocket_max,
            network_type: otherInsuranceFormData.network_type,
            prescription_coverage: otherInsuranceFormData.prescription_coverage,
            dental_coverage: otherInsuranceFormData.dental_coverage,
            vision_coverage: otherInsuranceFormData.vision_coverage,
            liability_coverage: otherInsuranceFormData.liability_coverage,
            collision_coverage: otherInsuranceFormData.collision_coverage,
            comprehensive_coverage: otherInsuranceFormData.comprehensive_coverage,
            uninsured_motorist: otherInsuranceFormData.uninsured_motorist,
            property_coverage: otherInsuranceFormData.property_coverage,
            liability_limit: otherInsuranceFormData.liability_limit,
            waiting_period: otherInsuranceFormData.waiting_period,
            benefit_period: otherInsuranceFormData.benefit_period,
            roadside_assistance: otherInsuranceFormData.roadside_assistance,
            rental_car_coverage: otherInsuranceFormData.rental_car_coverage,
            towing_coverage: otherInsuranceFormData.towing_coverage,
            medical_payments: otherInsuranceFormData.medical_payments,
            personal_injury_protection: otherInsuranceFormData.personal_injury_protection
          }
        }]);
      if (error) throw error;
      setShowOtherInsuranceModal(false);
      setOtherInsuranceFormData({
        insurance_category: '',
        provider: '',
        policy_number: '',
        coverage_amount: '',
        premium: '',
        payment_frequency: 'monthly',
        start_date: '',
        renewal_date: '',
        beneficiaries: [''],
        notes: '',
        document_url: '',
        file_path: '',
        file_name: '',
        file_size: 0,
        file_type: '',
        coverage_type: '',
        deductible: '',
        co_pay: '',
        out_of_pocket_max: '',
        network_type: '',
        prescription_coverage: false,
        dental_coverage: false,
        vision_coverage: false,
        liability_coverage: '',
        collision_coverage: '',
        comprehensive_coverage: '',
        uninsured_motorist: '',
        property_coverage: '',
        liability_limit: '',
        waiting_period: '',
        benefit_period: '',
        roadside_assistance: false,
        rental_car_coverage: false,
        towing_coverage: false,
        medical_payments: '',
        personal_injury_protection: ''
      });
      // Reload documents
      const { data } = await supabase
        .from('insurance_documents')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });
      setDocuments(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save insurance policy');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get insurance category label
  const getInsuranceCategoryLabel = (category: string) => {
    const found = INSURANCE_CATEGORIES.find(cat => cat.value === category);
    return found ? found.label : category;
  };

  // Helper to get insurance category icon
  const getInsuranceCategoryIcon = (category: string) => {
    const found = INSURANCE_CATEGORIES.find(cat => cat.value === category);
    return found ? found.icon : '📋';
  };

  // Document management functions
  const handleDownload = async (doc: InsuranceDocument) => {
    try {
      setDownloading(doc.id);
      if (!doc.file_url) {
        throw new Error('No file URL available');
      }
      
      const response = await fetch(doc.file_url);
      if (!response.ok) throw new Error('Failed to download file');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || 'insurance-document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download document');
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async (doc: InsuranceDocument) => {
    try {
      setDeleting(doc.id);
      
      // Delete from storage if file exists
      if (doc.file_path) {
        const { error: storageError } = await supabase.storage
          .from('insurance-documents')
          .remove([doc.file_path]);
        if (storageError) console.error('Storage delete error:', storageError);
      }
      
      // Delete from database
      const { error } = await supabase
        .from('insurance_documents')
        .delete()
        .eq('id', doc.id);
      
      if (error) throw error;
      
      // Reload documents
      const { data } = await supabase
        .from('insurance_documents')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });
      setDocuments(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete document');
    } finally {
      setDeleting(null);
    }
  };

  const handleView = (doc: InsuranceDocument) => {
    if (doc.file_url) {
      setPdfViewerUrl(doc.file_url);
    }
  };

  const handleShare = (doc: InsuranceDocument) => {
    setShareDocId(doc.id);
    setShowShareModal(true);
  };

  const handleShareFormChange = (field: string, value: string) => {
    setShareForm(prev => ({ ...prev, [field]: value }));
  };

  const handleShareSubmit = async () => {
    try {
      if (!shareDocId) return;
      
      // Here you would implement the actual sharing logic
      console.log('Sharing document:', shareDocId, shareForm);
      
      setShowShareModal(false);
      setShareForm({ email: '', accessLevel: 'view', note: '' });
      setShareDocId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to share document');
    }
  };

  const toggleExpanded = (docId: string) => {
    const newExpanded = new Set(expandedDocs);
    if (newExpanded.has(docId)) {
      newExpanded.delete(docId);
    } else {
      newExpanded.add(docId);
    }
    setExpandedDocs(newExpanded);
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return <FileText className="w-6 h-6 text-red-600" />;
    if (fileType.includes('word') || fileType.includes('document')) return <FileText className="w-6 h-6 text-blue-600" />;
    if (fileType.includes('image')) return <FileText className="w-6 h-6 text-green-600" />;
    return <FileText className="w-6 h-6 text-gray-600" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">Life Insurance</h2>
            <p className="text-gray-600 mb-2">
              Manage your life insurance policies and beneficiaries. <br />
              <span className="text-xs text-amber-700">Tip: Keep your beneficiaries up to date to ensure your wishes are legally protected.</span>
            </p>
            <div className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-center"
                onClick={() => setShowLifeModal(true)}
              >
                <Briefcase size={20} className="mr-2" />
                Add Life Insurance
              </Button>
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">Other Insurances</h2>
            <p className="text-gray-600 mb-2">
              Manage all your other insurance policies including health, auto, home, and more. <br />
              <span className="text-xs text-amber-700">Tip: Comprehensive insurance management helps protect your assets and provides peace of mind.</span>
            </p>
            <div className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-center"
                onClick={() => setShowOtherInsuranceModal(true)}
              >
                <Plus size={20} className="mr-2" />
                Add Other Insurance
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4">Your Insurance Policies</h2>
          <div className="space-y-4">
            {policies.map(policy => (
              <motion.div
                key={policy.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="text-2xl">
                    {policy.type === 'life' ? '💼' : getInsuranceCategoryIcon(policy.insurance_category || 'other')}
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {policy.provider} - {policy.type === 'life' ? 'Life Insurance' : getInsuranceCategoryLabel(policy.insurance_category || 'other')}
                    </h3>
                    <p className="text-sm text-gray-500">
                      Policy #: {policy.policy_number} | Coverage: ${policy.coverage_amount}
                    </p>
                    {policy.document_url && (
                      <a href={policy.document_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-xs">View Document</a>
                    )}
                  </div>
                </div>
                <Button variant="outline" size="sm">
                  View Details
                </Button>
              </motion.div>
            ))}
            {policies.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No insurance policies added yet
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-6">
          <div className="flex items-start gap-3 text-amber-800 bg-amber-50 p-4 rounded-lg">
            <AlertCircle className="text-amber-500 flex-shrink-0" size={20} />
            <div>
              <h3 className="font-medium mb-2">Insurance Review Reminder</h3>
              <p className="text-sm">
                Regular review of your insurance policies ensures adequate coverage and up-to-date beneficiary information. 
                We recommend reviewing your policies annually or when major life changes occur. <br />
                <span className="text-xs text-amber-700">Consider consulting a legal advisor to ensure your policies align with your estate plan.</span>
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Life Insurance Modal */}
      <Modal
        isOpen={showLifeModal}
        onClose={() => setShowLifeModal(false)}
        title="Add Life Insurance Policy"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Insurance Provider
            </label>
            <input
              type="text"
              value={lifeFormData.provider}
              onChange={(e) => setLifeFormData({ ...lifeFormData, provider: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Policy Number
            </label>
            <input
              type="text"
              value={lifeFormData.policy_number}
              onChange={(e) => setLifeFormData({ ...lifeFormData, policy_number: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Coverage Amount
            </label>
            <input
              type="text"
              value={lifeFormData.coverage_amount}
              onChange={(e) => setLifeFormData({ ...lifeFormData, coverage_amount: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., $500,000"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Premium Amount
              </label>
              <input
                type="text"
                value={lifeFormData.premium}
                onChange={(e) => setLifeFormData({ ...lifeFormData, premium: e.target.value })}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Frequency
              </label>
              <select
                value={lifeFormData.payment_frequency}
                onChange={(e) => setLifeFormData({ ...lifeFormData, payment_frequency: e.target.value })}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annually">Annually</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={lifeFormData.start_date}
                onChange={(e) => setLifeFormData({ ...lifeFormData, start_date: e.target.value })}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Renewal Date
              </label>
              <input
                type="date"
                value={lifeFormData.renewal_date}
                onChange={(e) => setLifeFormData({ ...lifeFormData, renewal_date: e.target.value })}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Beneficiaries
            </label>
            {lifeFormData.beneficiaries.map((beneficiary, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={beneficiary}
                  onChange={(e) => {
                    const newBeneficiaries = [...lifeFormData.beneficiaries];
                    newBeneficiaries[index] = e.target.value;
                    setLifeFormData({ ...lifeFormData, beneficiaries: newBeneficiaries });
                  }}
                  className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Full name"
                />
                <button
                  onClick={() => {
                    const newBeneficiaries = lifeFormData.beneficiaries.filter((_, i) => i !== index);
                    setLifeFormData({ ...lifeFormData, beneficiaries: newBeneficiaries });
                  }}
                  className="text-red-500 hover:text-red-700"
                >
                  <X size={20} />
                </button>
              </div>
            ))}
            <button
              onClick={() => setLifeFormData({ 
                ...lifeFormData, 
                beneficiaries: [...lifeFormData.beneficiaries, ''] 
              })}
              className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
            >
              <Plus size={16} />
              Add Beneficiary
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Additional Notes
            </label>
            <textarea
              value={lifeFormData.notes}
              onChange={(e) => setLifeFormData({ ...lifeFormData, notes: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Policy Document
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                onChange={(e) => handleFileUpload(e, setLifeFormData, lifeFormData)}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                className="hidden"
                id="file-upload-life"
                disabled={uploading}
              />
              <label
                htmlFor="file-upload-life"
                className={`cursor-pointer flex flex-col items-center justify-center ${uploading ? 'opacity-50' : ''}`}
              >
                {uploading ? (
                  <div className="w-12 h-12 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin mb-4" />
                ) : (
                  <Upload size={40} className="text-gray-400 mb-4" />
                )}
                <p className="text-sm text-gray-600">
                  {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  PDF, DOC, DOCX, or images (max. 50MB)
                </p>
              </label>
            </div>
            {lifeFormData.file_name && (
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-green-700">
                  <FileText size={16} />
                  <span className="font-medium">{lifeFormData.file_name}</span>
                  {lifeFormData.file_size && (
                    <span className="text-xs">({formatFileSize(lifeFormData.file_size)})</span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowLifeModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveLifeInsurance}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Policy'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Other Insurance Modal */}
      <Modal
        isOpen={showOtherInsuranceModal}
        onClose={() => setShowOtherInsuranceModal(false)}
        title="Add Other Insurance Policy"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Insurance Type
            </label>
            <select
              value={otherInsuranceFormData.insurance_category}
              onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, insurance_category: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Insurance Type</option>
              {INSURANCE_CATEGORIES.map(category => (
                <option key={category.value} value={category.value}>
                  {category.icon} {category.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Insurance Provider
            </label>
            <input
              type="text"
              value={otherInsuranceFormData.provider}
              onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, provider: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Policy Number
            </label>
            <input
              type="text"
              value={otherInsuranceFormData.policy_number}
              onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, policy_number: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Health Insurance Specific Fields */}
          {otherInsuranceFormData.insurance_category === 'health' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Coverage Type
                </label>
                <select
                  value={otherInsuranceFormData.coverage_type}
                  onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, coverage_type: e.target.value })}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Coverage Type</option>
                  <option value="individual">Individual</option>
                  <option value="family">Family</option>
                  <option value="employer">Employer Group</option>
                  <option value="medicare">Medicare</option>
                  <option value="medicaid">Medicaid</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Deductible
                  </label>
                  <input
                    type="text"
                    value={otherInsuranceFormData.deductible}
                    onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, deductible: e.target.value })}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., $1,500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Co-pay
                  </label>
                  <input
                    type="text"
                    value={otherInsuranceFormData.co_pay}
                    onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, co_pay: e.target.value })}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., $25"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Out-of-Pocket Maximum
                </label>
                <input
                  type="text"
                  value={otherInsuranceFormData.out_of_pocket_max}
                  onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, out_of_pocket_max: e.target.value })}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., $6,000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Network Type
                </label>
                <select
                  value={otherInsuranceFormData.network_type}
                  onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, network_type: e.target.value })}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Network Type</option>
                  <option value="ppo">PPO</option>
                  <option value="hmo">HMO</option>
                  <option value="epo">EPO</option>
                  <option value="pos">POS</option>
                  <option value="hdhp">HDHP</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Coverage Options</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={otherInsuranceFormData.prescription_coverage}
                      onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, prescription_coverage: e.target.checked })}
                      className="mr-2"
                    />
                    Prescription Coverage
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={otherInsuranceFormData.dental_coverage}
                      onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, dental_coverage: e.target.checked })}
                      className="mr-2"
                    />
                    Dental Coverage
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={otherInsuranceFormData.vision_coverage}
                      onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, vision_coverage: e.target.checked })}
                      className="mr-2"
                    />
                    Vision Coverage
                  </label>
                </div>
              </div>
            </>
          )}

          {/* Auto Insurance Specific Fields */}
          {otherInsuranceFormData.insurance_category === 'auto' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Liability Coverage
                  </label>
                  <input
                    type="text"
                    value={otherInsuranceFormData.liability_coverage}
                    onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, liability_coverage: e.target.value })}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., $100,000/$300,000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Collision Coverage
                  </label>
                  <input
                    type="text"
                    value={otherInsuranceFormData.collision_coverage}
                    onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, collision_coverage: e.target.value })}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., $500 deductible"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Comprehensive Coverage
                  </label>
                  <input
                    type="text"
                    value={otherInsuranceFormData.comprehensive_coverage}
                    onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, comprehensive_coverage: e.target.value })}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., $500 deductible"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Uninsured Motorist
                  </label>
                  <input
                    type="text"
                    value={otherInsuranceFormData.uninsured_motorist}
                    onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, uninsured_motorist: e.target.value })}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., $50,000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Medical Payments
                  </label>
                  <input
                    type="text"
                    value={otherInsuranceFormData.medical_payments}
                    onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, medical_payments: e.target.value })}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., $5,000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Personal Injury Protection
                  </label>
                  <input
                    type="text"
                    value={otherInsuranceFormData.personal_injury_protection}
                    onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, personal_injury_protection: e.target.value })}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., $10,000"
                  />
                </div>
              </div>
            </>
          )}

          {/* Home/Renters Insurance Specific Fields */}
          {(otherInsuranceFormData.insurance_category === 'home' || otherInsuranceFormData.insurance_category === 'renters') && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Property Coverage
                </label>
                <input
                  type="text"
                  value={otherInsuranceFormData.property_coverage}
                  onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, property_coverage: e.target.value })}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., $300,000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Liability Limit
                </label>
                <input
                  type="text"
                  value={otherInsuranceFormData.liability_limit}
                  onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, liability_limit: e.target.value })}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., $100,000"
                />
              </div>
            </>
          )}

          {/* Disability Insurance Specific Fields */}
          {otherInsuranceFormData.insurance_category === 'disability' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Coverage Type
                </label>
                <select
                  value={otherInsuranceFormData.coverage_type}
                  onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, coverage_type: e.target.value })}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Coverage Type</option>
                  <option value="short-term">Short-term Disability</option>
                  <option value="long-term">Long-term Disability</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Waiting Period
                  </label>
                  <input
                    type="text"
                    value={otherInsuranceFormData.waiting_period}
                    onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, waiting_period: e.target.value })}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 90 days"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Benefit Period
                  </label>
                  <input
                    type="text"
                    value={otherInsuranceFormData.benefit_period}
                    onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, benefit_period: e.target.value })}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 2 years"
                  />
                </div>
              </div>
            </>
          )}

          {/* Travel Insurance Specific Fields */}
          {otherInsuranceFormData.insurance_category === 'travel' && (
            <>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Coverage Options</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={otherInsuranceFormData.roadside_assistance}
                      onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, roadside_assistance: e.target.checked })}
                      className="mr-2"
                    />
                    Roadside Assistance
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={otherInsuranceFormData.rental_car_coverage}
                      onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, rental_car_coverage: e.target.checked })}
                      className="mr-2"
                    />
                    Rental Car Coverage
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={otherInsuranceFormData.towing_coverage}
                      onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, towing_coverage: e.target.checked })}
                      className="mr-2"
                    />
                    Towing Coverage
                  </label>
                </div>
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Coverage Amount
            </label>
            <input
              type="text"
              value={otherInsuranceFormData.coverage_amount}
              onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, coverage_amount: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., $500,000 or 60% of salary"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Premium Amount
              </label>
              <input
                type="text"
                value={otherInsuranceFormData.premium}
                onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, premium: e.target.value })}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Frequency
              </label>
              <select
                value={otherInsuranceFormData.payment_frequency}
                onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, payment_frequency: e.target.value })}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annually">Annually</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={otherInsuranceFormData.start_date}
                onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, start_date: e.target.value })}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Renewal Date
              </label>
              <input
                type="date"
                value={otherInsuranceFormData.renewal_date}
                onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, renewal_date: e.target.value })}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Beneficiaries (if applicable)
            </label>
            {otherInsuranceFormData.beneficiaries.map((beneficiary, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={beneficiary}
                  onChange={(e) => {
                    const newBeneficiaries = [...otherInsuranceFormData.beneficiaries];
                    newBeneficiaries[index] = e.target.value;
                    setOtherInsuranceFormData({ ...otherInsuranceFormData, beneficiaries: newBeneficiaries });
                  }}
                  className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Full name"
                />
                <button
                  onClick={() => {
                    const newBeneficiaries = otherInsuranceFormData.beneficiaries.filter((_, i) => i !== index);
                    setOtherInsuranceFormData({ ...otherInsuranceFormData, beneficiaries: newBeneficiaries });
                  }}
                  className="text-red-500 hover:text-red-700"
                >
                  <X size={20} />
                </button>
              </div>
            ))}
            <button
              onClick={() => setOtherInsuranceFormData({ 
                ...otherInsuranceFormData, 
                beneficiaries: [...otherInsuranceFormData.beneficiaries, ''] 
              })}
              className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
            >
              <Plus size={16} />
              Add Beneficiary
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Additional Notes
            </label>
            <textarea
              value={otherInsuranceFormData.notes}
              onChange={(e) => setOtherInsuranceFormData({ ...otherInsuranceFormData, notes: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Policy Document
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                onChange={(e) => handleFileUpload(e, setOtherInsuranceFormData, otherInsuranceFormData)}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                className="hidden"
                id="file-upload-other"
                disabled={uploading}
              />
              <label
                htmlFor="file-upload-other"
                className={`cursor-pointer flex flex-col items-center justify-center ${uploading ? 'opacity-50' : ''}`}
              >
                {uploading ? (
                  <div className="w-12 h-12 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin mb-4" />
                ) : (
                  <Upload size={40} className="text-gray-400 mb-4" />
                )}
                <p className="text-sm text-gray-600">
                  {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  PDF, DOC, DOCX, or images (max. 50MB)
                </p>
              </label>
            </div>
            {otherInsuranceFormData.file_name && (
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-green-700">
                  <FileText size={16} />
                  <span className="font-medium">{otherInsuranceFormData.file_name}</span>
                  {otherInsuranceFormData.file_size && (
                    <span className="text-xs">({formatFileSize(otherInsuranceFormData.file_size)})</span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowOtherInsuranceModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveOtherInsurance}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Policy'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* PDF Viewer Modal */}
      {pdfViewerUrl && (
        <Modal
          isOpen={!!pdfViewerUrl}
          onClose={() => setPdfViewerUrl(null)}
          title="Document Viewer"
          maxWidth="4xl"
        >
          <div className="h-96">
            <iframe
              src={pdfViewerUrl}
              className="w-full h-full border-0"
              title="Document Viewer"
            />
          </div>
        </Modal>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <Modal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          title="Share Document"
          maxWidth="md"
        >
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <input
                type="email"
                value={shareForm.email}
                onChange={(e) => handleShareFormChange('email', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter email address"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Access Level</label>
              <select
                value={shareForm.accessLevel}
                onChange={(e) => handleShareFormChange('accessLevel', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="view">View Only</option>
                <option value="edit">Edit</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Note (Optional)</label>
              <textarea
                value={shareForm.note}
                onChange={(e) => handleShareFormChange('note', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                placeholder="Add a note about this document..."
              />
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowShareModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleShareSubmit}
                className="flex-1"
              >
                Share Document
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center gap-2">
          <AlertCircle size={20} />
          <p>{error}</p>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-700 hover:text-red-900"
          >
            <X size={20} />
          </button>
        </div>
      )}
    </div>
  );
}